/* Store kit (library/stores/kit.md): the cart progress bar, the free gift and the offer builder.
   Theme-neutral: it watches the cart through fetch, so it works with any theme script that changes the cart
   through /cart/add, /cart/change, /cart/update or /cart/clear (Ajax API), and asks no hooks of the theme.
   - Progress bars [data-kit-progress]: filled from /cart.js on load and after every cart change.
   - Free gift (Theme settings > Store kit): when the cart qualifies, the gift variant is added once with the line
     property _kit_gift; when it stops qualifying, that line goes. A Shopify automatic discount (Buy X get Y,
     100 % off the gift) makes it free at checkout: `node bin/store-kit.js gift <store> --apply` makes it. A shopper
     who removes the gift keeps it removed until the cart drops below the threshold.
     The theme's own request waits for the gift, and a change/update answer carries the cart after it, so the
     theme's drawer shows the gift without knowing about it. 'kit:cart' (detail: the cart) fires after each sync.
   - Offer builder [data-kit-offers]: the picked card sets the product form's quantity; prices follow the variant.
     A text the theme editor fixed for an option (its price, crossed price or savings: [data-kit-fixed]) stays.
   - Add-on switches [data-product-addons] (snippets/product-addons.liquid, checkbox rows under the offer cards): a
     row's price text follows the picked set, and the add-to-cart buttons' total includes the ticked rows. */
(function () {
  'use strict';
  if (window.__kit) return;
  var cfgEl = document.getElementById('KitConfig');
  var cfg = {};
  try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { cfg = {}; }
  var fxRate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  var fxCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'PLN';
  var fxStep = new Intl.NumberFormat('en', { style: 'currency', currency: fxCurrency }).resolvedOptions().maximumFractionDigits === 0 ? 100 : 1;
  function presentmentDiscount(cents) { return Math.round(cents * fxRate / fxStep) * fxStep; }
  var kit = window.__kit = { cfg: cfg, cart: null };
  var root = (cfg.root || '/').replace(/\/?$/, '/');
  var gift = cfg.gift || {};
  var ship = cfg.ship || {};
  var giftOn = !!(gift.enabled && gift.variant);
  var addons = window.themeAddons;
  var cartQueue = Promise.resolve();
  function enqueue(job) {
    var next = cartQueue.then(job);
    cartQueue = next.catch(function () {});
    return next;
  }
  kit.idle = function () { return cartQueue; };
  var DECLINED = 'kit_gift_declined';

  /* ---------- money ---------- */
  function fmtNum(cents, dec, thou, sep) {
    var n = (Math.round(cents) / 100).toFixed(dec);
    var parts = n.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    return parts.join(sep);
  }
  function money(cents) {
    var f = String(cfg.moneyFormat || '{{amount_with_comma_separator}} zł').replace(/&nbsp;/g, '\u00a0');
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      switch (k) {
        case 'amount': return fmtNum(cents, 2, ',', '.');
        case 'amount_no_decimals': return fmtNum(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmtNum(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmtNum(cents, 0, '.', ',');
        case 'amount_with_space_separator': return fmtNum(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmtNum(cents, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmtNum(cents, 2, "'", '.');
        default: return fmtNum(cents, 2, ',', '.');
      }
    });
  }
  kit.money = money;
  function fill(s, amount, giftTitle) {
    return String(s || '').replace('__AMOUNT__', amount == null ? '' : amount).replace('__GIFT__', giftTitle || '');
  }

  /* ---------- the cart ---------- */
  var nativeFetch = window.fetch.bind(window);
  var CART_RE = /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  function isGiftLine(item) { return !!(item && item.properties && item.properties._kit_gift); }
  function getCart() {
    return nativeFetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }
  function post(path, body) {
    return nativeFetch(root + path, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }
  function ss(k, v) {
    try { if (v === undefined) return sessionStorage.getItem(k); if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch (e) {}
    return null;
  }

  // What counts toward the gift and the free shipping: the cart after discounts, gift lines left out
  function measure(cart) {
    var giftCents = 0, qty = 0;
    (cart.items || []).forEach(function (it) {
      if (isGiftLine(it)) giftCents += it.final_line_price || 0;
      else if (!(addons && addons.isAddon(it))) qty += it.quantity;
    });
    return { amount: Math.max(0, (cart.total_price || 0) - giftCents), qty: qty };
  }
  function qualifies(cart) {
    var m = measure(cart);
    return gift.mode === 'quantity' ? m.qty >= (gift.threshold || 0) : m.amount >= (gift.threshold || 0);
  }

  // Bring the gift line in line with the cart: returns a promise of { cart, changed }
  function syncGift(cart) {
    if (!giftOn) return Promise.resolve({ cart: cart, changed: false });
    var lines = (cart.items || []).filter(isGiftLine);
    var want = qualifies(cart) && m0(cart) > 0;
    if (!want) ss(DECLINED, null);
    var declined = ss(DECLINED) === '1';
    var job = null;
    if (want && !declined) {
      if (!lines.length) job = post('cart/add.js', { items: [{ id: gift.variant, quantity: 1, properties: { _kit_gift: '1' } }] });
      else if (lines.length > 1 || lines[0].quantity !== 1 || lines[0].variant_id !== gift.variant) {
        var upd = {};
        lines.forEach(function (l, i) { upd[l.key] = (i === 0 && l.variant_id === gift.variant) ? 1 : 0; });
        job = post('cart/update.js', { updates: upd });
      }
    } else if (lines.length) {
      var rm = {};
      lines.forEach(function (l) { rm[l.key] = 0; });
      job = post('cart/update.js', { updates: rm });
    }
    if (!job) return Promise.resolve({ cart: cart, changed: false });
    return job.then(getCart).then(function (c) { return { cart: c, changed: true }; }, function () { return { cart: cart, changed: false }; });
  }
  // the cart's non-gift item count (a cart with only the gift in it doesn't keep the gift)
  function m0(cart) { return measure(cart).qty; }

  // A shopper removing the gift line: remember it for the session
  function noteDecline(kind, body) {
    if (!giftOn || !kit.cart || (kind !== 'change' && kind !== 'update')) return;
    var data = body;
    if (typeof body === 'string') { try { data = JSON.parse(body); } catch (e) { data = null; } }
    else if (body && typeof body.get === 'function') {
      data = { line: body.get('line'), id: body.get('id'), quantity: body.get('quantity') };
    }
    if (!data) return;
    var items = kit.cart.items || [];
    var hit = function (it) { if (isGiftLine(it)) ss(DECLINED, '1'); };
    if (kind === 'change' && String(data.quantity) === '0') {
      if (data.line) hit(items[parseInt(data.line, 10) - 1]);
      else if (data.id != null) items.forEach(function (it) { if (it.key === String(data.id) || String(it.variant_id) === String(data.id)) hit(it); });
    }
    if (kind === 'update' && data.updates && typeof data.updates === 'object' && !Array.isArray(data.updates)) {
      Object.keys(data.updates).forEach(function (k) {
        if (String(data.updates[k]) === '0') items.forEach(function (it) { if (it.key === k || String(it.variant_id) === k) hit(it); });
      });
    }
  }

  function publish(cart) {
    kit.cart = cart;
    renderBars(cart);
    try { document.dispatchEvent(new CustomEvent('kit:cart', { detail: cart })); } catch (e) {}
  }

  async function syncExtras(cart) {
    var paid = addons ? await addons.sync(cart, post, getCart) : { cart: cart, changed: false };
    var free = await syncGift(paid.cart);
    return { cart: free.cart, changed: paid.changed || free.changed };
  }
  // One queue for startup, theme mutations, paid add-ons and the existing free gift.
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var match = method === 'POST' ? url.match(CART_RE) : null;
    if (!match) return nativeFetch(input, init);
    return enqueue(async function () {
      if (addons) addons.setBusy(true);
      try {
        var kind = match[1];
        noteDecline(kind, init && init.body);
        var res = await nativeFetch(input, init);
        if (!res.ok) return res;
        var s = await syncExtras(await getCart());
        publish(s.cart);
        if (kind === 'add') return res;
        var body = {};
        try { body = JSON.parse(init.body); } catch (e) {}
        if (body.sections) {
          return await nativeFetch(root + 'cart/update.js', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: {}, sections: body.sections, sections_url: body.sections_url })
          });
        }
        return new Response(JSON.stringify(s.cart), { status: 200, headers: { 'Content-Type': 'application/json' } });
      } catch (err) {
        // The real item may already have changed. Show the actual cart and an actionable error.
        try { publish(await getCart()); } catch (e) {}
        if (addons) addons.fail();
        throw err;
      } finally { if (addons) addons.setBusy(false); }
    });
  };

  /* ---------- progress bars ---------- */
  function renderBars(cart) {
    var bars = document.querySelectorAll('[data-kit-progress]');
    if (!bars.length) return;
    var m = measure(cart || { items: [], total_price: 0 });
    var empty = !m.qty;
    var goals = [];
    if (ship.threshold > 0) goals.push({ at: ship.threshold, away: ship.away, done: ship.done });
    var declined = ss(DECLINED) === '1';
    // a gift the shopper removed is not announced again while the cart stays over the threshold
    if (giftOn && gift.mode !== 'quantity' && gift.threshold > 0 && !(declined && m.amount >= gift.threshold)) goals.push({ at: gift.threshold, away: gift.away, done: gift.done, gift: true });
    goals.sort(function (a, b) { return a.at - b.at; });
    var text = '', pct = 0, marks = '';
    if (goals.length) {
      var top = goals[goals.length - 1].at;
      var next = null;
      for (var i = 0; i < goals.length; i++) if (m.amount < goals[i].at) { next = goals[i]; break; }
      text = next ? fill(next.away, money(next.at - m.amount), gift.title) : fill(goals[goals.length - 1].done, '', gift.title);
      pct = Math.min(100, Math.round((m.amount / top) * 100));
      if (goals.length > 1) goals.slice(0, -1).forEach(function (g) { marks += '<i style="left:' + Math.round((g.at / top) * 100) + '%"' + (m.amount >= g.at ? ' class="is-done"' : '') + '></i>'; });
    }
    var qtyLine = '';
    if (giftOn && gift.mode === 'quantity' && gift.threshold > 0) {
      var left = gift.threshold - m.qty;
      qtyLine = (left <= 0 && declined) ? '' : left > 0 ? fill(gift.away, left + '\u00a0' + ((cfg.t && cfg.t.pieces) || 'szt.'), gift.title) : fill(gift.done, '', gift.title);
      if (!goals.length) pct = Math.min(100, Math.round((m.qty / gift.threshold) * 100));
    }
    bars.forEach(function (bar) {
      var place = bar.getAttribute('data-place');
      var t = bar.querySelector('[data-kit-progress-text]');
      var f = bar.querySelector('[data-kit-progress-fill]');
      var mk = bar.querySelector('[data-kit-progress-marks]');
      if (empty) {
        var e = place === 'product' ? (ship.empty || '') : '';
        bar.hidden = !e;
        bar.classList.add('is-empty');
        if (t) t.textContent = e;
        if (f) f.style.width = '0%';
        if (mk) mk.innerHTML = '';
        return;
      }
      var line = [text, qtyLine].filter(Boolean).join(' \u00b7 ');
      bar.hidden = !line;
      bar.classList.remove('is-empty');
      bar.classList.toggle('is-complete', pct >= 100);
      if (t) t.textContent = line;
      if (f) f.style.width = pct + '%';
      if (mk) mk.innerHTML = marks;
    });
  }

  /* ---------- offer builder ---------- */
  function offerForm(box) {
    var id = box.getAttribute('data-form');
    var form = id ? document.getElementById(id) : null;
    return form || box.closest('form') || null;
  }
  function setQty(box) {
    var form = offerForm(box);
    var picked = box.querySelector('[data-kit-offer]:checked');
    box.querySelectorAll('.kit-offer').forEach(function (l) { l.classList.toggle('is-selected', !!l.querySelector('[data-kit-offer]:checked')); });
    if (!form || !picked) return;
    var q = form.querySelector('[name="quantity"]');
    if (!q) { q = document.createElement('input'); q.type = 'hidden'; q.name = 'quantity'; form.appendChild(q); }
    q.value = picked.getAttribute('data-qty') || picked.value;
    var extras = paintAddons(form, Math.max(1, parseInt(q.value, 10) || 1));
    if (box.hasAttribute('data-atc-total')) atcTotal(box, form, picked, extras);
  }
  // The add-on switches of the form ([data-product-addons] names it in data-form): each row's price text follows the
  // picked set (a per-piece add-on: "+9,90 zł × 2 = 19,80 zł"). Returns the ticked rows' sum in cents.
  function paintAddons(form, qty) {
    var fid = form.getAttribute('id');
    var sum = 0;
    if (!fid) return sum;
    document.querySelectorAll('[data-product-addons][data-form="' + fid + '"] [data-product-addon]').forEach(function (input) {
      var price = parseInt(input.getAttribute('data-price'), 10) || 0;
      var n = input.getAttribute('data-per-piece') === 'true' ? qty : 1;
      var row = input.closest('label');
      var out = row && row.querySelector('[data-product-addon-price]');
      if (out) out.textContent = '+' + money(price) + (n > 1 ? ' \u00d7 ' + n + ' = ' + money(price * n) : '');
      if (input.checked) sum += price * n;
    });
    return sum;
  }
  // the picked set's price in cents: the selected variant's price x quantity minus the option's discount
  function setCents(box, form, picked) {
    var idInput = form.querySelector('[name="id"]');
    var pricesEl = box.querySelector('[data-kit-prices]');
    var prices = null;
    try { prices = pricesEl ? JSON.parse(pricesEl.textContent) : null; } catch (e) { prices = null; }
    var unit = prices && idInput ? prices[String(idInput.value)] : null;
    if (unit == null) return null;
    var base = unit * (parseInt(picked.getAttribute('data-qty'), 10) || 1);
    var offA = presentmentDiscount(parseInt(picked.getAttribute('data-off-amount'), 10) || 0);
    var offP = parseFloat(picked.getAttribute('data-off-percent')) || 0;
    return base - (offA || Math.round(base * offP / 100));
  }
  // the add-to-cart buttons of the form (the sticky bar's too) show the picked set's total, plus the ticked add-on
  // switches' (extras, in cents)
  function atcTotal(box, form, picked, extras) {
    var card = picked.closest('.kit-offer');
    var totalEl = card && card.querySelector('[data-kit-total]');
    var total = totalEl ? totalEl.textContent.trim() : '';
    if (extras > 0) {
      var cents = setCents(box, form, picked);
      total = cents != null ? money(cents + extras) : (total ? total + ' + ' : '') + money(extras);
    }
    var btns = Array.prototype.slice.call(form.querySelectorAll('button[type="submit"], button:not([type])'));
    // getAttribute: form.id would be the form's <input name="id">
    var fid = form.getAttribute('id');
    if (fid) btns = btns.concat(Array.prototype.slice.call(document.querySelectorAll('button[form="' + fid + '"]')));
    btns.forEach(function (b) {
      var span = b.querySelector('.kit-atc-total');
      if (!span) { span = document.createElement('span'); span.className = 'kit-atc-total'; b.appendChild(span); }
      span.textContent = total ? '\u00a0\u00b7 ' + total : '';
    });
  }
  function repriceOffers(box) {
    var form = offerForm(box);
    var idInput = form && form.querySelector('[name="id"]');
    var pricesEl = box.querySelector('[data-kit-prices]');
    if (!idInput || !pricesEl) return;
    var prices; try { prices = JSON.parse(pricesEl.textContent); } catch (e) { return; }
    var vid = String(idInput.value);
    // the one-piece option shows the selected variant's photo (e.g. the chosen scent)
    if (vid !== box.getAttribute('data-variant')) {
      box.setAttribute('data-variant', vid);
      var imgsEl = box.querySelector('[data-kit-variant-images]');
      var imgs = null; try { imgs = imgsEl ? JSON.parse(imgsEl.textContent) : null; } catch (e) { imgs = null; }
      if (imgs && imgs[vid]) box.querySelectorAll('img[data-kit-variant-img]').forEach(function (im) { var u = String(imgs[vid]); if (im.getAttribute('src') !== u) im.setAttribute('src', u); });
    }
    var unit = prices[vid];
    if (unit == null || String(unit) === box.getAttribute('data-unit')) { mirrorAll(); return; }
    box.setAttribute('data-unit', String(unit));
    // the offer's own words (the "Quantity offers" block), else the kit's
    var tEach = box.getAttribute('data-t-each') || (cfg.t && cfg.t.pieces) || 'szt.';
    var tSave = box.getAttribute('data-t-save');
    box.querySelectorAll('[data-kit-offer]').forEach(function (r) {
      var qty = parseInt(r.getAttribute('data-qty'), 10) || 1;
      var base = unit * qty;
      var offA = presentmentDiscount(parseInt(r.getAttribute('data-off-amount'), 10) || 0);
      var offP = parseFloat(r.getAttribute('data-off-percent')) || 0;
      var off = offA || Math.round(base * offP / 100);
      var total = base - off;
      var card = r.closest('.kit-offer');
      // a text fixed in the theme editor ([data-kit-fixed]) is never recomputed
      var set = function (sel, v) { var el = card.querySelector(sel); if (el && !el.hasAttribute('data-kit-fixed')) el.textContent = v; };
      set('[data-kit-total]', money(total));
      set('[data-kit-ref]', money(base));
      set('[data-kit-unit]', money(total / qty) + ' / ' + tEach);
      // a percent saving follows the variant's price (an amount is the same for every variant)
      if ((offA || offP) && tSave) set('[data-kit-save]', tSave + ' ' + money(off));
    });
    setQty(box);
    mirrorAll();
  }

  /* ---------- the compact mirror ([data-kit-offers-compact], e.g. in the sticky bar) ----------
     Chips for the options of the page's first [data-kit-offer-group] (the kit's offer builder, or a theme's own
     radio group marked with it; a radio's data-kit-short names its chip). A chip checks its radio and fires the
     radio's change, so the theme's and the kit's own handlers run as if the shopper picked it there. */
  function sourceGroup() { return document.querySelector('[data-kit-offer-group]'); }
  function mirrorAll() {
    var group = sourceGroup();
    if (!group) return;
    var radios = Array.prototype.slice.call(group.querySelectorAll('input[type="radio"]'));
    document.querySelectorAll('[data-kit-offers-compact]').forEach(function (box) {
      if (!box.__kitBuilt) {
        box.__kitBuilt = true;
        box.classList.add('kit-compact');
        box.setAttribute('role', 'group');
        radios.forEach(function (r, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'kit-chip';
          b.setAttribute('data-i', String(i));
          b.textContent = r.getAttribute('data-kit-short') || r.getAttribute('data-title') || String(i + 1);
          if (r.disabled) b.disabled = true;
          box.appendChild(b);
        });
        box.addEventListener('click', function (e) {
          var b = e.target.closest('.kit-chip');
          if (!b) return;
          var r = radios[parseInt(b.getAttribute('data-i'), 10)];
          if (!r || r.disabled) return;
          if (!r.checked) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
          mirrorAll();
        });
      }
      var picked = radios.find(function (r) { return r.checked; });
      var card = picked && picked.closest('.kit-offer');
      var source = card && card.querySelector('.kit-offer__media img');
      var sticky = box.closest('[data-sticky-atc]');
      var photoHost = sticky && sticky.querySelector('[data-sticky-photo]');
      var photo = photoHost && photoHost.querySelector('img');
      if (source && photoHost && !photo) {
        photo = document.createElement('img'); photo.width = 120; photo.height = 120;
        photoHost.replaceChildren(photo);
      }
      if (source && photo) {
        // Use the exact offer-card URL, including variant changes at the same price.
        photo.removeAttribute('srcset');
        photo.removeAttribute('sizes');
        if (photo.getAttribute('src') !== source.getAttribute('src')) photo.src = source.getAttribute('src');
        photo.alt = source.alt;
      }
      box.querySelectorAll('.kit-chip').forEach(function (b) {
        var r = radios[parseInt(b.getAttribute('data-i'), 10)];
        b.setAttribute('aria-pressed', r && r.checked ? 'true' : 'false');
      });
    });
  }
  document.addEventListener('change', function (e) {
    if (e.target && e.target.type === 'radio' && e.target.closest('[data-kit-offer-group]')) setTimeout(mirrorAll, 0);
    // an add-on switch ticked or unticked (the theme also fires it after it unticked them): the total follows
    if (e.target && e.target.closest && e.target.closest('[data-product-addons]')) document.querySelectorAll('[data-kit-offers]').forEach(function (box) { setQty(box); });
  });
  function initOffers() {
    document.querySelectorAll('[data-kit-offers]').forEach(function (box) {
      if (box.__kit) return;
      box.__kit = true;
      box.addEventListener('change', function (e) { if (e.target.matches('[data-kit-offer]')) { setQty(box); mirrorAll(); } });
      setQty(box);
      repriceOffers(box);
      var form = offerForm(box);
      if (form) {
        // theme variant pickers set the hidden id input; follow it
        form.addEventListener('change', function () { setTimeout(function () { repriceOffers(box); }, 0); });
        document.addEventListener('change', function () { setTimeout(function () { repriceOffers(box); }, 0); });
        form.addEventListener('submit', function () { setQty(box); }, true);
      }
    });
  }

  /* ---------- start ---------- */
  function start() {
    initOffers();
    mirrorAll();
    enqueue(async function () {
      if (addons) addons.setBusy(true);
      try {
        var s = await syncExtras(await getCart());
        publish(s.cart);
        if (s.changed && document.querySelector('.cart-page__form')) location.reload();
      } catch (err) {
        try { publish(await getCart()); } catch (e) {}
        if (addons) addons.fail();
      } finally { if (addons) addons.setBusy(false); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', function () { initOffers(); mirrorAll(); if (kit.cart) renderBars(kit.cart); });
})();
